import React, { useMemo, useState } from 'react';
import {
    Button,
    Card,
    Col,
    Divider,
    message,
    Modal,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Typography
} from 'antd';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import PropTypes from 'prop-types';

import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { linearGradientDef } from '@nivo/core';
import { ResponsiveLine } from '@nivo/line';
import { useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import { useUserState } from '../../store';
import axios from '../../utilities/axios';
import { FORMAT } from '../../utilities/functions';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;

const REASON_MAP = {
    equityBelow9500:
        'Your funded account equity has fallen below the $9,500 threshold, which is the maximum allowed drawdown level for your program.',
    equityBelow10500:
        'Your funded account equity has fallen below the $10,500 threshold, which is the maximum allowed drawdown level for your program.',
    maxResets3: 'You have exceeded the maximum limit of 3 free account resets.',
    didNotMeet36Days:
        'Your account did not meet the minimum trading duration of 36 unique trading days, which is required to qualify.',
    didNotMeet10Day2TradesRollingAvg:
        'Your account did not maintain an average of at least 2 trades per day over any rolling 10-day period, as required.',
    stocksBelow5:
        'You have traded stocks below $5, which is not permitted under the program rules.',
    tradeOver30PctTotalPnL:
        'A single winning trade accounts for more than 30% of your total account profit, which exceeds the payout concentration limit.',
    leveragedETF: 'You traded a leveraged ETF, which is not permitted under the program rules.',
    other: 'This funded account has a risk violation.'
};

const NY_TZ = 'America/New_York';

const ViolationExplanation = ({
    accountId,
    buttonText = 'View Violation Report',
    riskViolationReason
}) => {
    const { getUser } = useUserState();
    const user = getUser();
    const [modalOpen, setModalOpen] = useState(false);

    const hasEquityViolation = Array.isArray(riskViolationReason)
        ? riskViolationReason.includes('equityBelow10500') ||
          riskViolationReason.includes('equityBelow9500')
        : false;

    const mapReasonTexts = Array.isArray(riskViolationReason)
        ? riskViolationReason.map((reason) => REASON_MAP[reason] || reason)
        : [];

    const fetchViolationReport = async () => {
        const { data } = await axios.get(`crud/violation-report/${accountId}`, {
            headers: { token: user?.token }
        });
        return data;
    };

    const {
        data: report,
        isLoading,
        error
    } = useQuery(['violation-report', accountId], fetchViolationReport, {
        enabled: modalOpen && !!accountId && !!user?.token && hasEquityViolation,
        retry: false,
        onError: (err) => {
            message.error(err?.response?.data?.error || 'Failed to load violation report');
        }
    });

    const handleOpen = () => setModalOpen(true);

    const handleClose = () => setModalOpen(false);

    const equityCurveData = useMemo(() => {
        if (!report?.charts?.fullEquityCurve) return null;

        const {
            labels,
            data: equityData,
            violationLineValue,
            startingLineValue
        } = report.charts.fullEquityCurve;
        const equityPoints = labels.map((label, idx) => ({
            // If label is '2024-03-20T09:30:00-04:00', then Date(label) is already NY wall time.
            x: new Date(label),
            y: equityData[idx]
        }));

        return {
            equity: [{ id: 'Equity', data: equityPoints }],
            violationLine: violationLineValue,
            startingLine: startingLineValue
        };
    }, [report]);

    const violationZoomData = useMemo(() => {
        if (!report?.charts?.violationZoom) return null;

        const {
            labels,
            data: equityData,
            violationLineValue,
            startingLineValue
        } = report.charts.violationZoom;
        const equityPoints = labels.map((label, idx) => ({
            x: new Date(label),
            y: equityData[idx]
        }));

        return {
            equity: [{ id: 'Equity', data: equityPoints }],
            violationLine: violationLineValue,
            startingLine: startingLineValue
        };
    }, [report]);

    const positionsColumns = [
        {
            title: 'Symbol',
            dataIndex: 'symbol',
            key: 'symbol'
        },
        {
            title: 'Side',
            dataIndex: 'side',
            key: 'side',
            render: (side) => <Tag color={side === 'LONG' ? 'green' : 'red'}>{side}</Tag>
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity'
        },
        {
            title: 'Avg Price',
            dataIndex: 'averagePrice',
            key: 'averagePrice',
            render: (val) => `$${val?.toFixed(2)}`
        },
        {
            title: 'Violation Price',
            dataIndex: 'violationPrice',
            key: 'violationPrice',
            render: (val) => `$${val?.toFixed(2)}`
        },
        {
            title: 'Unrealized P&L',
            dataIndex: 'unrealizedPnl',
            key: 'unrealizedPnl',
            render: (val) => (
                <Text style={{ color: val < 0 ? '#d4380d' : '#389e0d', fontWeight: 500 }}>
                    {val < 0 ? '' : '+'}${val?.toFixed(2)}
                </Text>
            )
        }
    ];

    // --- CHANGE: Only load/report *anything* if there is a violation ---
    const shouldLoadReport = modalOpen && !!accountId && !!user?.token && hasEquityViolation;
    const startingEquityLabel = report?.charts?.fullEquityCurve?.startingLineValue || 12500;
    const violationThresholdLabel = report?.charts?.fullEquityCurve?.violationLineValue || 10500;

    // Helper: Format tick labels for NY timezone for nivo/line (value is a Date, so use dayjs(value).tz(NY_TZ))
    function formatNYTick(fmt) {
        return (value) => dayjs(value).tz(NY_TZ).format(fmt);
    }

    // Helper: Format tooltip x-axis for NY timezone (value is a Date)
    function tooltipNYDate(x, fmt) {
        return dayjs(x).tz(NY_TZ).format(fmt);
    }

    return (
        <>
            <Button type="primary" onClick={handleOpen}>
                {buttonText}
            </Button>

            <Modal
                title="Violation Report"
                open={modalOpen}
                onCancel={handleClose}
                width="90%"
                style={{ top: 20 }}
                footer={[
                    <Button key="close" onClick={handleClose}>
                        Close
                    </Button>
                ]}
            >
                <div style={{ padding: 16, maxHeight: '80vh', overflowY: 'auto' }}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Summary */}
                        {Array.isArray(mapReasonTexts) && mapReasonTexts.length > 0 && (
                            <Card title={<Title level={4}>Reasons</Title>}>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {mapReasonTexts.map((reason, idx) => (
                                        <li
                                            key={idx}
                                            style={{
                                                marginBottom:
                                                    idx < mapReasonTexts.length - 1 ? 8 : 0
                                            }}
                                        >
                                            <Text>{reason}</Text>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        )}

                        {/* Violation Report Section */}
                        {hasEquityViolation && (
                            <>
                                {/* 1. Show nothing if the report is fetched and hasViolation is false */}
                                {shouldLoadReport && (
                                    <>
                                        {isLoading && (
                                            <div style={{ padding: 40, textAlign: 'center' }}>
                                                <Spin
                                                    size="large"
                                                    tip="Loading violation report..."
                                                />
                                            </div>
                                        )}

                                        {!isLoading && error && (
                                            <Card>
                                                <Text type="danger">
                                                    Failed to load violation report. Please try
                                                    again.
                                                </Text>
                                            </Card>
                                        )}

                                        {/* Only render detailed report if hasViolation is true */}
                                        {!isLoading && !error && report && report.hasViolation && (
                                            <>
                                                {/* Account Summary Card (Account ID/Name Removed) */}
                                                <Card
                                                    title={<Title level={4}>Account Summary</Title>}
                                                >
                                                    <Row gutter={[16, 16]}>
                                                        <Col span={8}>
                                                            <Text strong>Starting Equity:</Text>
                                                            <br />
                                                            <Text>
                                                                {FORMAT.NUMBER_TO_CURRENCY(
                                                                    report.summary?.startingEquity
                                                                )}
                                                            </Text>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Text strong>Final Equity:</Text>
                                                            <br />
                                                            <Text>
                                                                {FORMAT.NUMBER_TO_CURRENCY(
                                                                    report.summary?.finalEquity
                                                                )}
                                                            </Text>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Text strong>Total Realized P&L:</Text>
                                                            <br />
                                                            <Text
                                                                style={{
                                                                    color:
                                                                        report.summary
                                                                            ?.totalRealizedPnl >= 0
                                                                            ? '#389e0d'
                                                                            : '#d4380d',
                                                                    fontWeight: 500
                                                                }}
                                                            >
                                                                {report.summary?.totalRealizedPnl >=
                                                                0
                                                                    ? '+'
                                                                    : ''}
                                                                $
                                                                {report.summary?.totalRealizedPnl?.toFixed(
                                                                    2
                                                                ) || '-'}
                                                            </Text>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Text strong>Max Equity:</Text>
                                                            <br />
                                                            <Text>
                                                                $
                                                                {report.summary?.maxEquity?.value?.toFixed(
                                                                    2
                                                                ) || '-'}
                                                            </Text>
                                                            {report.summary?.maxEquity?.time && (
                                                                <Text
                                                                    type="secondary"
                                                                    style={{
                                                                        display: 'block',
                                                                        fontSize: 12
                                                                    }}
                                                                >
                                                                    {dayjs(
                                                                        report.summary.maxEquity
                                                                            .time
                                                                    )
                                                                        .tz(NY_TZ)
                                                                        .format(
                                                                            'MMM D, YYYY HH:mm'
                                                                        )}
                                                                </Text>
                                                            )}
                                                        </Col>
                                                        <Col span={8}>
                                                            <Text strong>Min Equity:</Text>
                                                            <br />
                                                            <Text
                                                                style={{
                                                                    color:
                                                                        report.summary?.minEquity
                                                                            ?.value !== undefined &&
                                                                        report.summary?.minEquity
                                                                            ?.value <
                                                                            report.charts
                                                                                ?.fullEquityCurve
                                                                                ?.violationLineValue
                                                                            ? '#d4380d'
                                                                            : undefined
                                                                }}
                                                            >
                                                                $
                                                                {report.summary?.minEquity?.value?.toFixed(
                                                                    2
                                                                ) || '-'}
                                                            </Text>
                                                            {report.summary?.minEquity?.time && (
                                                                <Text
                                                                    type="secondary"
                                                                    style={{
                                                                        display: 'block',
                                                                        fontSize: 12
                                                                    }}
                                                                >
                                                                    {dayjs(
                                                                        report.summary.minEquity
                                                                            .time
                                                                    )
                                                                        .tz(NY_TZ)
                                                                        .format(
                                                                            'MMM D, YYYY HH:mm'
                                                                        )}
                                                                </Text>
                                                            )}
                                                        </Col>
                                                        <Col span={8}>
                                                            <Text strong>Total Trades:</Text>
                                                            <br />
                                                            <Text>
                                                                {report.summary?.totalTrades}
                                                            </Text>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Text strong>Symbols Traded:</Text>
                                                            <br />
                                                            <Text>
                                                                {report.summary?.symbolsTraded?.join(
                                                                    ', '
                                                                ) || 'N/A'}
                                                            </Text>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Text strong>Violation Status:</Text>
                                                            <br />
                                                            {report.hasViolation ? (
                                                                <Tag
                                                                    icon={<WarningOutlined />}
                                                                    color="error"
                                                                >
                                                                    Violation Detected
                                                                </Tag>
                                                            ) : (
                                                                <Tag
                                                                    icon={<CheckCircleOutlined />}
                                                                    color="success"
                                                                >
                                                                    No Violation
                                                                </Tag>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </Card>

                                                {/* Full Equity Curve */}
                                                {equityCurveData && (
                                                    <Card
                                                        title={
                                                            <Title level={4}>
                                                                Full Equity Curve
                                                            </Title>
                                                        }
                                                    >
                                                        <div style={{ height: 400 }}>
                                                            <ResponsiveLine
                                                                data={equityCurveData.equity}
                                                                curve="linear"
                                                                margin={{
                                                                    top: 20,
                                                                    right: 20,
                                                                    bottom: 60,
                                                                    left: 80
                                                                }}
                                                                xScale={{
                                                                    type: 'time',
                                                                    format: '%Y-%m-%d',
                                                                    useUTC: false
                                                                }}
                                                                yScale={{
                                                                    type: 'linear',
                                                                    min: 'auto',
                                                                    max: 'auto'
                                                                }}
                                                                axisBottom={{
                                                                    // Format as NY time
                                                                    format: formatNYTick(
                                                                        'MMM D, YYYY'
                                                                    ),
                                                                    tickValues: 'every 7 days'
                                                                }}
                                                                axisLeft={{
                                                                    format: (value) =>
                                                                        `$${value.toLocaleString()}`
                                                                }}
                                                                enableGridX={false}
                                                                enableGridY={true}
                                                                enablePoints={false}
                                                                lineWidth={2}
                                                                colors={['#2962FF']}
                                                                enableArea={true}
                                                                areaOpacity={0.1}
                                                                defs={[
                                                                    linearGradientDef(
                                                                        'equityGradient',
                                                                        [
                                                                            {
                                                                                offset: 0,
                                                                                color: '#2962FF',
                                                                                opacity: 0.3
                                                                            },
                                                                            {
                                                                                offset: 100,
                                                                                color: '#2962FF',
                                                                                opacity: 0
                                                                            }
                                                                        ]
                                                                    )
                                                                ]}
                                                                fill={[
                                                                    {
                                                                        match: '*',
                                                                        id: 'equityGradient'
                                                                    }
                                                                ]}
                                                                markers={[
                                                                    {
                                                                        axis: 'y',
                                                                        value: equityCurveData.violationLine,
                                                                        lineStyle: {
                                                                            stroke: '#d4380d',
                                                                            strokeWidth: 2,
                                                                            strokeDasharray: '5 5'
                                                                        },
                                                                        legend: `Violation Threshold (${FORMAT.NUMBER_TO_CURRENCY(
                                                                            violationThresholdLabel
                                                                        )})`,
                                                                        legendPosition: 'top-left'
                                                                    },
                                                                    {
                                                                        axis: 'y',
                                                                        value: equityCurveData.startingLine,
                                                                        lineStyle: {
                                                                            stroke: '#52c41a',
                                                                            strokeWidth: 2,
                                                                            strokeDasharray: '5 5'
                                                                        },
                                                                        legend: `Starting Equity (${FORMAT.NUMBER_TO_CURRENCY(
                                                                            startingEquityLabel
                                                                        )})`,
                                                                        legendPosition: 'top-left'
                                                                    }
                                                                ]}
                                                                useMesh={true}
                                                                tooltip={({ point }) => (
                                                                    <div
                                                                        style={{
                                                                            background: '#fff',
                                                                            padding: '8px 12px',
                                                                            border: '1px solid #ccc',
                                                                            borderRadius: 4
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <strong>Date:</strong>{' '}
                                                                            {tooltipNYDate(
                                                                                point.data.x,
                                                                                'MMM D, YYYY HH:mm'
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <strong>Equity:</strong>{' '}
                                                                            ${point.data.yFormatted}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            />
                                                        </div>
                                                    </Card>
                                                )}

                                                {/* Violation Details */}
                                                {report.hasViolation && report.violationDetails && (
                                                    <Card
                                                        title={
                                                            <Title level={4}>
                                                                Equity Violation Details
                                                            </Title>
                                                        }
                                                    >
                                                        <Space
                                                            direction="vertical"
                                                            size="middle"
                                                            style={{ width: '100%' }}
                                                        >
                                                            <Row gutter={[16, 16]}>
                                                                <Col span={8}>
                                                                    <Text strong>
                                                                        Violation Time:
                                                                    </Text>
                                                                    <br />
                                                                    <Text>
                                                                        {dayjs(
                                                                            report.violationDetails
                                                                                .violationTime
                                                                        )
                                                                            .tz(NY_TZ)
                                                                            .format(
                                                                                'MMM D, YYYY HH:mm:ss'
                                                                            )}
                                                                    </Text>
                                                                </Col>
                                                                <Col span={8}>
                                                                    <Text strong>
                                                                        Equity at Violation:
                                                                    </Text>
                                                                    <br />
                                                                    <Text
                                                                        style={{
                                                                            color: '#d4380d',
                                                                            fontWeight: 500
                                                                        }}
                                                                    >
                                                                        $
                                                                        {report.violationDetails.equityAtViolation?.toFixed(
                                                                            2
                                                                        )}
                                                                    </Text>
                                                                </Col>
                                                                <Col span={8}>
                                                                    <Text strong>
                                                                        Trade That Triggered:
                                                                    </Text>
                                                                    <br />
                                                                    {report.violationDetails
                                                                        .tradeThatTriggered ? (
                                                                        <a
                                                                            href={`/trades/${report.violationDetails.tradeThatTriggered}`}
                                                                        >
                                                                            View Trade
                                                                        </a>
                                                                    ) : (
                                                                        <Text>N/A</Text>
                                                                    )}
                                                                </Col>
                                                                <Col span={8}>
                                                                    <Text strong>
                                                                        Realized P&L at Violation:
                                                                    </Text>
                                                                    <br />
                                                                    <Text
                                                                        style={{
                                                                            color:
                                                                                report
                                                                                    .violationDetails
                                                                                    .realizedPnl >=
                                                                                0
                                                                                    ? '#389e0d'
                                                                                    : '#d4380d'
                                                                        }}
                                                                    >
                                                                        {report.violationDetails
                                                                            .realizedPnl >= 0
                                                                            ? '+'
                                                                            : ''}
                                                                        $
                                                                        {report.violationDetails.realizedPnl?.toFixed(
                                                                            2
                                                                        ) || '-'}
                                                                    </Text>
                                                                </Col>
                                                                <Col span={8}>
                                                                    <Text strong>
                                                                        Unrealized P&L at Violation:
                                                                    </Text>
                                                                    <br />
                                                                    <Text
                                                                        style={{
                                                                            color:
                                                                                report
                                                                                    .violationDetails
                                                                                    .unrealizedPnl >=
                                                                                0
                                                                                    ? '#389e0d'
                                                                                    : '#d4380d'
                                                                        }}
                                                                    >
                                                                        {report.violationDetails
                                                                            .unrealizedPnl >= 0
                                                                            ? '+'
                                                                            : ''}
                                                                        $
                                                                        {report.violationDetails.unrealizedPnl?.toFixed(
                                                                            2
                                                                        ) || '-'}
                                                                    </Text>
                                                                </Col>
                                                                {report.violationDetails
                                                                    .equityAtTradeStart && (
                                                                    <Col span={8}>
                                                                        <Text strong>
                                                                            Equity at Trade Start:
                                                                        </Text>
                                                                        <br />
                                                                        <Text>
                                                                            $
                                                                            {report.violationDetails.equityAtTradeStart?.toFixed(
                                                                                2
                                                                            ) || '-'}
                                                                        </Text>
                                                                    </Col>
                                                                )}
                                                            </Row>

                                                            {report.violationDetails.positions &&
                                                                report.violationDetails.positions
                                                                    .length > 0 && (
                                                                    <>
                                                                        <Divider />
                                                                        <div>
                                                                            <Text
                                                                                strong
                                                                                style={{
                                                                                    fontSize: 16
                                                                                }}
                                                                            >
                                                                                Open Positions at
                                                                                Violation:
                                                                            </Text>
                                                                            <Table
                                                                                columns={
                                                                                    positionsColumns
                                                                                }
                                                                                dataSource={report.violationDetails.positions.map(
                                                                                    (pos, idx) => ({
                                                                                        ...pos,
                                                                                        key: idx
                                                                                    })
                                                                                )}
                                                                                pagination={false}
                                                                                size="small"
                                                                                style={{
                                                                                    marginTop: 16
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                )}
                                                        </Space>
                                                    </Card>
                                                )}

                                                {/* Violation Zoom Chart */}
                                                {violationZoomData && (
                                                    <Card
                                                        title={
                                                            <Title level={4}>
                                                                Equity Around Violation Time
                                                            </Title>
                                                        }
                                                    >
                                                        <div style={{ height: 400 }}>
                                                            <ResponsiveLine
                                                                data={violationZoomData.equity}
                                                                curve="linear"
                                                                margin={{
                                                                    top: 20,
                                                                    right: 20,
                                                                    bottom: 60,
                                                                    left: 80
                                                                }}
                                                                xScale={{
                                                                    type: 'time',
                                                                    format: '%Y-%m-%d',
                                                                    useUTC: false
                                                                }}
                                                                yScale={{
                                                                    type: 'linear',
                                                                    min: 'auto',
                                                                    max: 'auto'
                                                                }}
                                                                axisBottom={{
                                                                    // Show NY time with hour,
                                                                    format: formatNYTick(
                                                                        'MM-D-YYYY HH:mm'
                                                                    ),
                                                                    tickValues: 'every 30 minutes'
                                                                }}
                                                                axisLeft={{
                                                                    format: (value) =>
                                                                        `$${value.toLocaleString()}`
                                                                }}
                                                                enableGridX={false}
                                                                enableGridY={true}
                                                                enablePoints={true}
                                                                pointSize={6}
                                                                lineWidth={2}
                                                                colors={['#2962FF']}
                                                                enableArea={true}
                                                                areaOpacity={0.1}
                                                                defs={[
                                                                    linearGradientDef(
                                                                        'equityGradient',
                                                                        [
                                                                            {
                                                                                offset: 0,
                                                                                color: '#2962FF',
                                                                                opacity: 0.3
                                                                            },
                                                                            {
                                                                                offset: 100,
                                                                                color: '#2962FF',
                                                                                opacity: 0
                                                                            }
                                                                        ]
                                                                    )
                                                                ]}
                                                                fill={[
                                                                    {
                                                                        match: '*',
                                                                        id: 'equityGradient'
                                                                    }
                                                                ]}
                                                                markers={[
                                                                    {
                                                                        axis: 'y',
                                                                        value: violationZoomData.violationLine,
                                                                        lineStyle: {
                                                                            stroke: '#d4380d',
                                                                            strokeWidth: 2,
                                                                            strokeDasharray: '5 5'
                                                                        },
                                                                        legend: `Violation Threshold (${FORMAT.NUMBER_TO_CURRENCY(
                                                                            violationThresholdLabel
                                                                        )})`,
                                                                        legendPosition: 'top-left'
                                                                    },
                                                                    {
                                                                        axis: 'y',
                                                                        value: violationZoomData.startingLine,
                                                                        lineStyle: {
                                                                            stroke: '#52c41a',
                                                                            strokeWidth: 2,
                                                                            strokeDasharray: '5 5'
                                                                        },
                                                                        legend: `Starting Equity (${FORMAT.NUMBER_TO_CURRENCY(
                                                                            startingEquityLabel
                                                                        )})`,
                                                                        legendPosition: 'top-left'
                                                                    }
                                                                ]}
                                                                useMesh={true}
                                                                tooltip={({ point }) => (
                                                                    <div
                                                                        style={{
                                                                            background: '#fff',
                                                                            padding: '8px 12px',
                                                                            border: '1px solid #ccc',
                                                                            borderRadius: 4
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <strong>Date:</strong>{' '}
                                                                            {tooltipNYDate(
                                                                                point.data.x,
                                                                                'MMM D, YYYY HH:mm'
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <strong>Equity:</strong>{' '}
                                                                            ${point.data.yFormatted}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            />
                                                        </div>
                                                    </Card>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Space>
                </div>
            </Modal>
        </>
    );
};

ViolationExplanation.propTypes = {
    accountId: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    riskViolationReason: PropTypes.array.isRequired
};

export default ViolationExplanation;
